/* ============================================================
   ProviderPrintersPanel — printer list + add/edit form
   ============================================================ */

import { useEffect, useState } from 'react';
import { SlicingProfilesEditor } from './SlicingProfilesEditor';
import { api } from '../api/client';
import type { Material } from '../types';
import type { ProviderProfileDetail, PrinterDetail, PrinterTechnology, CreatePrinter, SlicerEngine } from '../types/provider';
import { assignmentPlan, unassignedMaterials } from './materialsChipsView';
import type { MaterialIdSet } from './materialsChipsView';
import { MaterialChipsSelector, MaterialChip } from './MaterialsChips';
import { ProviderFleetOverviewCard } from './ProviderFleetOverviewCard';
import {
  MAX_PICKER_RESULTS,
  catalogDefaults,
  distinctBrands,
  filterCatalogEntries,
  formatBuildVolume,
  isCatalogLinked,
  normalizePrinterTechnology,
  pickerSummary,
} from './printerCatalogPickerView';
import type { PrinterFormMode, PrinterModelCatalogResponse, PrinterModelEntry } from './printerCatalogPickerView';

const TECHNOLOGIES: { value: PrinterTechnology; label: string }[] = [
  { value: 'Fdm', label: 'FDM' },
  { value: 'Sla', label: 'SLA / DLP' },
  { value: 'Sls', label: 'SLS / MJF' },
];

/** 0258c2c7: slicer choice saved on the printer profile (Bambu Studio / Cura). */
const SLICERS: { value: SlicerEngine; label: string; hint: string }[] = [
  { value: 'BambuStudio', label: 'Bambu Studio', hint: 'Серверная подготовка файлов (профили слайсера)' },
  { value: 'Cura', label: 'UltiMaker Cura', hint: 'Второй слайсер — выбор сохраняется на профиле' },
];

const normalizeSlicerEngine = (v: unknown): SlicerEngine =>
  v === 'Cura' || v === 1 ? 'Cura' : 'BambuStudio';

export function ProviderPrintersPanel({ profile }: { profile: ProviderProfileDetail | null }) {
  // Enum ordinals from the API are normalized once at ingestion so the
  // technology label on each printer card resolves (was blank for numbers).
  const normalizePrinters = (list: PrinterDetail[]): PrinterDetail[] =>
    list.map((p) => ({ ...p, technology: normalizePrinterTechnology(p.technology), slicerEngine: normalizeSlicerEngine(p.slicerEngine) }));
  const [printers, setPrinters] = useState<PrinterDetail[]>(normalizePrinters(profile?.printers ?? []));
  const [showForm, setShowForm] = useState(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [fleetRefreshKey, setFleetRefreshKey] = useState(0);
  const [fleetNotice, setFleetNotice] = useState<string | null>(null);

  // PF-4: global material catalog feeds the quick-assign chips (spec §5.2).
  useEffect(() => {
    let cancelled = false;
    api.getMaterials()
      .then((ms) => { if (!cancelled) setMaterials(ms); })
      .catch(() => { /* chips degrade to empty; form stays usable */ });
    return () => { cancelled = true; };
  }, []);

  const bumpFleet = () => setFleetRefreshKey((k) => k + 1);

  const handleAdded = (p: PrinterDetail, warning: string | null) => {
    setPrinters([...printers, p]);
    setShowForm(false);
    setFleetNotice(warning);
    bumpFleet();
  };

  // PF-4: sequential AddMaterialToPrinterAsync per picked chip (spec §5.2).
  const handleAssignMaterials = async (printerId: string, selected: MaterialIdSet): Promise<string | null> => {
    const failed: string[] = [];
    for (const a of assignmentPlan(selected)) {
      try {
        await api.addPrinterMaterial(printerId, a);
      } catch {
        failed.push(a.materialId);
      }
    }
    return failed.length > 0
      ? `Не привязано материалов: ${failed.length}. Их можно добавить позже на карточке принтера.`
      : null;
  };

  const handleLinkMaterial = async (printerId: string, materialId: string) => {
    try {
      const pm = await api.addPrinterMaterial(printerId, { materialId });
      setPrinters(printers.map((p) =>
        p.printerId === printerId
          ? { ...p, supportedMaterials: [...p.supportedMaterials, pm] }
          : p));
      bumpFleet();
    } catch { /* duplicate/race — chip row refreshes on next render */ }
  };

  const handleUnlinkMaterial = async (printerId: string, providerMaterialId: string) => {
    try {
      await api.removePrinterMaterial(providerMaterialId);
      setPrinters(printers.map((p) =>
        p.printerId === printerId
          ? { ...p, supportedMaterials: p.supportedMaterials.filter((m) => m.providerMaterialId !== providerMaterialId) }
          : p));
      bumpFleet();
    } catch { /* ignore */ }
  };

  const handleDeactivate = async (printerId: string) => {
    try {
      const printer = printers.find((p) => p.printerId === printerId);
      if (!printer) return;
      await api.updatePrinter(printerId, {
        name: printer.name, brand: printer.brand, model: printer.model,
        buildVolumeX: printer.buildVolumeX, buildVolumeY: printer.buildVolumeY,
        buildVolumeZ: printer.buildVolumeZ, hourlyRateRub: printer.hourlyRateRub,
        isActive: false, slicerEngine: printer.slicerEngine,
      });
      setPrinters(printers.map((p) => p.printerId === printerId ? { ...p, isActive: false } : p));
    } catch { /* ignore */ }
  };

  // PF-3 «Перевести в кастомный»: clears the catalog link (values stay,
  // provenance flips — PF-2 e6d6ab7d); unchanged fields pass the lock check.
  const handleUnlink = async (printerId: string) => {
    try {
      const printer = printers.find((p) => p.printerId === printerId);
      if (!printer) return;
      const updated = await api.updatePrinter(printerId, {
        name: printer.name, brand: printer.brand, model: printer.model,
        buildVolumeX: printer.buildVolumeX, buildVolumeY: printer.buildVolumeY,
        buildVolumeZ: printer.buildVolumeZ, hourlyRateRub: printer.hourlyRateRub,
        isActive: printer.isActive, unlinkFromCatalog: true, slicerEngine: printer.slicerEngine,
      });
      setPrinters(printers.map((p) => p.printerId === printerId ? updated : p));
      bumpFleet();
    } catch { /* ignore */ }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Мои принтеры</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} style={{ height: 40, padding: '8px 16px' }}>
          {showForm ? 'Отмена' : '+ Добавить'}
        </button>
      </div>

      {showForm && <AddPrinterForm materials={materials} onAdded={handleAdded} onAssignMaterials={handleAssignMaterials} />}

      {fleetNotice && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
          {fleetNotice}
        </p>
      )}

      {/* PF-4: fleet overview — linked vs custom, coverage, material union */}
      <ProviderFleetOverviewCard refreshKey={fleetRefreshKey} />

      {/* Printer list */}
      {printers.length === 0 && !showForm ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            Нет добавленных принтеров. Добавьте первый принтер, чтобы начать принимать заказы.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {printers.map((p) => (
            <PrinterCard
              key={p.printerId}
              printer={p}
              materials={materials}
              onDeactivate={handleDeactivate}
              onLinkMaterial={handleLinkMaterial}
              onUnlinkMaterial={handleUnlinkMaterial}
              onUnlink={handleUnlink}
              onProfilesSaved={() => {
                // 16215cbc: refresh the badge (hasSlicingProfiles) + fleet overview.
                bumpFleet();
                api.getProviderProfile()
                  .then((fresh) => setPrinters(normalizePrinters(fresh.printers ?? [])))
                  .catch(() => { /* badge stays stale until next mount */ });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Printer card ────────────────────────────────────────── */

function PrinterCard({
  printer, materials, onDeactivate, onLinkMaterial, onUnlinkMaterial, onUnlink, onProfilesSaved,
}: {
  printer: PrinterDetail;
  materials: Material[];
  onDeactivate: (id: string) => void;
  onLinkMaterial: (printerId: string, materialId: string) => void;
  onUnlinkMaterial: (printerId: string, providerMaterialId: string) => void;
  onUnlink: (printerId: string) => void;
  onProfilesSaved: () => void;
}) {
  // 16215cbc: slicer presets editor (machine/process/filament JSON).
  const [showProfiles, setShowProfiles] = useState(false);
  const linkedIds = new Set(printer.supportedMaterials.map((m) => m.materialId));
  const addable = unassignedMaterials(materials, linkedIds).slice(0, 6);
  // PF-3: catalog provenance (PF-2 CatalogSlug) — locked fields + unlink action.
  const catalogLinked = isCatalogLinked(printer);
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>{printer.name}</h3>
            <span className={`badge ${printer.isActive ? 'badge-success' : 'badge-warning'}`}>
              {printer.isActive ? 'Активен' : 'Выключен'}
            </span>
            {catalogLinked && (
              <span className="badge" title={`Из каталога принтеров · ${printer.catalogSlug}`}>
                Из каталога
              </span>
            )}
            <span
              className={`badge ${printer.hasSlicingProfiles ? 'badge-success' : 'badge-neutral'}`}
              title="Профили слайсера для подготовки g-code (16215cbc)"
            >
              {printer.hasSlicingProfiles ? 'Профили ✓' : 'Без профилей'}
            </span>
            {printer.slicerEngine === 'Cura' && (
              <span className="badge" title="0258c2c7: слайсер на профиле принтера">Слайсер: Cura</span>
            )}
          </div>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            {printer.brand} {printer.model} · {TECHNOLOGIES.find((t) => t.value === printer.technology)?.label}
          </p>
        </div>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
          {printer.hourlyRateRub}₽/ч
        </p>
      </div>

      {/* Build volume — PF-3: catalog-derived while linked (CATALOG_LOCKED_FIELDS) */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <Tag label="Объём" value={`${printer.buildVolumeX}×${printer.buildVolumeY}×${printer.buildVolumeZ}мм`} />
        {catalogLinked && (
          <Tag label="Из каталога" value="бренд, модель, технология и объём зафиксированы" />
        )}
      </div>

      {/* Materials — PF-4: linked chips are removable, dashed chips quick-add */}
      <div>
        <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-xs)' }}>
          Материалы
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {printer.supportedMaterials.map((m) => (
            <MaterialChip
              key={m.providerMaterialId}
              name={m.materialName}
              kind="removable"
              title="Убрать материал"
              onClick={() => onUnlinkMaterial(printer.printerId, m.providerMaterialId)}
            />
          ))}
          {printer.supportedMaterials.length === 0 && (
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Не выбраны — принтер печатает любым материалом
            </span>
          )}
          {addable.map((m) => (
            <MaterialChip
              key={m.id}
              name={m.name}
              kind="addable"
              title="Добавить материал"
              onClick={() => onLinkMaterial(printer.printerId, m.id)}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        {printer.isActive && (
          <button className="btn btn-tertiary" onClick={() => onDeactivate(printer.printerId)} style={{ alignSelf: 'flex-start', fontSize: 'var(--fs-caption)' }}>
            Деактивировать
          </button>
        )}
        {catalogLinked && (
          <button
            className="btn btn-tertiary"
            onClick={() => onUnlink(printer.printerId)}
            title="Отвязать от каталога и открыть поля для редактирования (значения сохранятся)"
            style={{ alignSelf: 'flex-start', fontSize: 'var(--fs-caption)' }}
          >
            Перевести в кастомный
          </button>
        )}
        <button
          className="btn btn-tertiary"
          onClick={() => setShowProfiles((v) => !v)}
          title="Flattened профили Bambu Studio для слайсинга заказов"
          style={{ alignSelf: 'flex-start', fontSize: 'var(--fs-caption)' }}
        >
          {showProfiles ? 'Скрыть профили слайсера' : 'Профили слайсера'}
        </button>
      </div>

      {showProfiles && <SlicingProfilesEditor printerId={printer.printerId} onSaved={onProfilesSaved} />}
    </div>
  );
}

/* ── Add printer form ───────────────────────────────────── */

function AddPrinterForm({
  materials, onAdded, onAssignMaterials,
}: {
  materials: Material[];
  onAdded: (p: PrinterDetail, warning: string | null) => void;
  onAssignMaterials: (printerId: string, selected: MaterialIdSet) => Promise<string | null>;
}) {
  const [mode, setMode] = useState<PrinterFormMode>('catalog');
  const [name, setName] = useState('');
  const [technology, setTechnology] = useState<PrinterTechnology>('Fdm');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [x, setX] = useState('250');
  const [y, setY] = useState('250');
  const [z, setZ] = useState('250');
  const [rate, setRate] = useState('300');
  const [slicer, setSlicer] = useState<SlicerEngine>('BambuStudio');
  const [selectedMaterials, setSelectedMaterials] = useState<MaterialIdSet>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PF-3 «Из каталога»: public reference data (PF-1 GET /api/v1/printer-catalog).
  const [catalog, setCatalog] = useState<PrinterModelCatalogResponse | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'catalog' || catalog || catalogError) return;
    let cancelled = false;
    api.getPrinterCatalog()
      .then((c) => { if (!cancelled) setCatalog(c); })
      .catch(() => {
        if (!cancelled) setCatalogError('Каталог моделей недоступен. Добавьте принтер вручную в режиме «Свой принтер».');
      });
    return () => { cancelled = true; };
  }, [mode, catalog, catalogError]);

  // API enum wire format is ordinal numbers (see normalizePrinterTechnology):
  // normalize once at the boundary so search/summary/chips/prefill all see
  // canonical 'Fdm'|'Sla'|'Sls' — the pre-fix crash was preset.includes on
  // undefined after selecting an entry whose technology arrived as 0.
  const entries = (catalog?.models ?? []).map((e) => ({
    ...e,
    technology: normalizePrinterTechnology(e.technology),
  }));
  const selectedEntry = entries.find((e) => e.slug === selectedSlug) ?? null;
  const visibleEntries = filterCatalogEntries(entries, { query, brand: brandFilter });

  const selectEntry = (entry: PrinterModelEntry) => {
    setSelectedSlug(entry.slug);
    setName(catalogDefaults(entry).name);
    setTechnology(entry.technology);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'catalog' && !selectedEntry) return;
    setSaving(true);
    setError(null);
    try {
      // Catalog mode: the server re-stamps Technology/Brand/Model/volume from
      // the slug (PF-2, tamper-proof) — posted values are display-only.
      const dto: CreatePrinter = mode === 'catalog' && selectedEntry
        ? {
            name,
            technology: selectedEntry.technology,
            brand: selectedEntry.brand,
            model: selectedEntry.model,
            buildVolumeX: selectedEntry.buildVolumeX,
            buildVolumeY: selectedEntry.buildVolumeY,
            buildVolumeZ: selectedEntry.buildVolumeZ,
            hourlyRateRub: parseFloat(rate),
            catalogSlug: selectedEntry.slug,
            slicerEngine: slicer,
          }
        : {
            name, technology, brand, model,
            buildVolumeX: parseFloat(x), buildVolumeY: parseFloat(y), buildVolumeZ: parseFloat(z),
            hourlyRateRub: parseFloat(rate),
            slicerEngine: slicer,
          };
      const result = await api.addPrinter(dto);
      // PF-4: quick-assign chips — sequential material links after creation
      // (spec §5.2); failures never lose the printer, just the chip batch.
      const warning = await onAssignMaterials(result.printerId, selectedMaterials);
      onAdded(result, warning);
      // reset
      setName(''); setBrand(''); setModel('');
      setQuery(''); setBrandFilter(null); setSelectedSlug(null);
      setSelectedMaterials(new Set()); setSlicer('BambuStudio');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const chipsTechnology = mode === 'catalog' && selectedEntry ? selectedEntry.technology : technology;
  const submitDisabled = saving || !name.trim()
    || (mode === 'catalog' ? !selectedEntry : !brand.trim());

  return (
    <form onSubmit={handleSubmit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)' }}>Новый принтер</h3>

      {/* PF-3 mode toggle (§5.1): «Из каталога» by default, «Свой принтер» = free-form */}
      <div role="tablist" style={{ display: 'flex', gap: 'var(--space-xs)' }}>
        <ModeButton active={mode === 'catalog'} onClick={() => setMode('catalog')}>Из каталога</ModeButton>
        <ModeButton active={mode === 'custom'} onClick={() => setMode('custom')}>Свой принтер</ModeButton>
      </div>

      {mode === 'catalog' ? (
        <>
          {catalogError && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 0 }}>{catalogError}</p>}
          {!catalog && !catalogError && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>Загрузка каталога моделей…</p>
          )}
          {catalog && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск: бренд, модель или псевдоним (напр. x1c)"
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                <BrandChip active={brandFilter === null} onClick={() => setBrandFilter(null)}>Все бренды</BrandChip>
                {distinctBrands(entries).map((b) => (
                  <BrandChip key={b} active={brandFilter === b} onClick={() => setBrandFilter(b)}>{b}</BrandChip>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', maxHeight: 280, overflowY: 'auto' }}>
                {visibleEntries.map((entry) => (
                  <button
                    key={entry.slug}
                    type="button"
                    onClick={() => selectEntry(entry)}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--fs-caption)',
                      cursor: 'pointer',
                      border: `1px solid ${selectedSlug === entry.slug ? 'var(--color-primary)' : 'var(--color-separator)'}`,
                      background: selectedSlug === entry.slug ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
                      color: selectedSlug === entry.slug ? 'var(--color-primary)' : 'var(--color-text-primary)',
                      fontWeight: selectedSlug === entry.slug ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                    }}
                  >
                    {pickerSummary(entry)}
                  </button>
                ))}
                {visibleEntries.length === 0 && (
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
                    Ничего не найдено. Уточните запрос или добавьте принтер в режиме «Свой принтер».
                  </p>
                )}
              </div>
              {selectedEntry && (
                <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <Tag label="Бренд" value={selectedEntry.brand} />
                  <Tag label="Модель" value={selectedEntry.model} />
                  <Tag label="Технология" value={TECHNOLOGIES.find((t) => t.value === selectedEntry.technology)?.label ?? selectedEntry.technology} />
                  <Tag label="Объём" value={formatBuildVolume(selectedEntry)} />
                </div>
              )}
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
                Показано до {MAX_PICKER_RESULTS} моделей. Характеристики фиксируются каталогом и не редактируются.
              </p>
            </div>
          )}
          <FormField label="Название *">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder={selectedEntry ? selectedEntry.model : 'Выберите модель из каталога'}
              style={inputStyle}
            />
          </FormField>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          <FormField label="Название *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Bambu X1C" style={inputStyle} />
          </FormField>
          <FormField label="Технология *">
            <select value={technology} onChange={(e) => setTechnology(e.target.value as PrinterTechnology)} style={inputStyle}>
              {TECHNOLOGIES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>
          <FormField label="Бренд *">
            <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} required placeholder="Bambu Lab" style={inputStyle} />
          </FormField>
          <FormField label="Модель *">
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} required placeholder="X1 Carbon" style={inputStyle} />
          </FormField>
          <FormField label="X (мм)">
            <input type="number" value={x} onChange={(e) => setX(e.target.value)} style={inputStyle} />
          </FormField>
          <FormField label="Y (мм)">
            <input type="number" value={y} onChange={(e) => setY(e.target.value)} style={inputStyle} />
          </FormField>
          <FormField label="Z (мм)">
            <input type="number" value={z} onChange={(e) => setZ(e.target.value)} style={inputStyle} />
          </FormField>
        </div>
      )}

      <FormField label="Ставка (₽/час)">
        <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} style={inputStyle} />
      </FormField>

      {/* 0258c2c7: slicer choice saved on the printer profile (Bambu / Cura) */}
      <FormField label="Слайсер">
        <select value={slicer} onChange={(e) => setSlicer(e.target.value as SlicerEngine)} style={inputStyle}>
          {SLICERS.map((s) => <option key={s.value} value={s.value} title={s.hint}>{s.label}</option>)}
        </select>
      </FormField>

      <MaterialChipsSelector
        materials={materials}
        selected={selectedMaterials}
        onChange={setSelectedMaterials}
        technology={chipsTechnology}
      />

      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}

      <button type="submit" className="btn btn-primary" disabled={submitDisabled}>
        {saving ? 'Добавление...' : 'Добавить принтер'}
      </button>
    </form>
  );
}

/* PF-3 mode toggle segment (§5.1) — light-theme pill per DESIGN-SYSTEM.md. */
function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '8px 16px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--fs-caption)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-separator)'}`,
        background: active ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
        color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

function BrandChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--fs-caption)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-separator)'}`,
        background: active ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
        color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

/* ── Small helpers ───────────────────────────────────────── */

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </label>
  );
}

function Tag({ label, value }: { label: string; value: string }) {
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--color-bg)',
      fontSize: 'var(--fs-caption)',
      color: 'var(--color-text-secondary)',
    }}>
      {label}: {value}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 44,
  padding: '10px 14px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};
